import { Component, Input, Output, EventEmitter, HostListener, ElementRef, OnInit, OnChanges, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { LucideAngularModule } from 'lucide-angular';

export interface AutoOption {
  value: any;
  label: string;
}

export interface AutoOptionGroup {
  label: string;
  items: AutoOption[];
}

@Component({
  selector: 'app-multi-autocomplete',
  standalone: true,
  imports: [CommonModule, FormsModule, LucideAngularModule],
  templateUrl: './app-multi-autocomplete.component.html',
  styleUrls: ['./app-multi-autocomplete.component.css'],
})
export class AppMultiAutocompleteComponent implements OnInit, OnChanges {
  @Input() options: AutoOption[] = [];
  @Input() optionGroups: AutoOptionGroup[] = [];
  @Input() placeholder: string = 'Buscar...';
  @Input() excludeKey: string = 'idService';
  @Input() preselected: Array<any> = [];

  @Output() selectionChange = new EventEmitter<AutoOption[]>();

  searchText: string = '';
  isOpen: boolean = false;
  selectedItems: AutoOption[] = [];
  private _preselectedProcessed = false;

  constructor(private elementRef: ElementRef) {}

  ngOnInit(): void {
    this.processPreselected();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['preselected'] && !this._preselectedProcessed) {
      this.processPreselected();
    }
    if (changes['optionGroups'] || changes['options']) {
      if (!this._preselectedProcessed) {
        this.processPreselected();
      }
    }
  }

  get hasGroups(): boolean {
    return this.optionGroups && this.optionGroups.length > 0;
  }

  getAllOptions(): AutoOption[] {
    if (this.hasGroups) {
      return (this.optionGroups || []).flatMap(g => g.items || []);
    }
    return this.options || [];
  }

  get filteredOptions(): AutoOption[] {
    return this.getAllOptions().filter(o =>
      !this.isSelected(o.value) &&
      (!this.searchText || o.label.toLowerCase().includes(this.searchText.toLowerCase()))
    );
  }

  get filteredGroups(): AutoOptionGroup[] {
    if (!this.hasGroups) return [];
    return (this.optionGroups || []).map(g => ({
      label: g.label,
      items: (g.items || []).filter(o =>
        !this.isSelected(o.value) &&
        (!this.searchText || o.label.toLowerCase().includes(this.searchText.toLowerCase()))
      )
    })).filter(g => g.items.length > 0);
  }

  isSelected(value: any): boolean {
    return this.selectedItems.some(s => this.isSameOption(s.value, value));
  }

  isSameOption(a: any, b: any): boolean {
    if (a === b) return true;
    if (typeof a === 'object' && typeof b === 'object' && a && b) {
      const key = this.excludeKey;
      if (key && a[key] != null && b[key] != null) return a[key] === b[key];
    }
    return false;
  }

  onInputFocus(): void {
    this.isOpen = true;
  }

  onInputClick(): void {
    this.isOpen = true;
  }

  processPreselected(): void {
    if (this._preselectedProcessed) return;
    if (!this.preselected || this.preselected.length === 0) return;
    const all = this.getAllOptions();
    if (all.length === 0) return;
    this.selectedItems = this.preselected
      .map(ps => {
        const found = all.find(o => this.isSameOption(o.value, ps));
        if (found) return { value: found.value, label: found.label };
        if (ps && ps.name && ps.idCalendar) {
          const f2 = all.find(o => o.value && o.value.idCalendar === ps.idCalendar);
          if (f2) return { value: f2.value, label: f2.label };
        }
        if (ps && ps.name) {
          return { value: ps, label: ps.name };
        }
        return null;
      })
      .filter(o => o !== null) as AutoOption[];
    this._preselectedProcessed = true;
  }

  selectOption(option: AutoOption): void {
    if (this.isSelected(option.value)) return;
    this.selectedItems = [...this.selectedItems, { value: option.value, label: option.label }];
    this.searchText = '';
    this.isOpen = false;
    this.emitChange();
    (this.elementRef.nativeElement.querySelector('input') as HTMLInputElement)?.blur();
  }

  removeItem(item: AutoOption): void {
    this.selectedItems = this.selectedItems.filter(s => !this.isSameOption(s.value, item.value));
    this.emitChange();
  }

  clearAll(): void {
    this.selectedItems = [];
    this.searchText = '';
    this.emitChange();
  }

  private emitChange(): void {
    this.selectionChange.emit(this.selectedItems);
  }

  @HostListener('document:mousedown', ['$event'])
  onClickOutside(event: MouseEvent): void {
    if (!this.elementRef.nativeElement.contains(event.target)) {
      this.isOpen = false;
    }
  }
}